/**
 * EOS Weekly To-Dos - Horizontal Slider by Person (Slack-style)
 * 
 * Features:
 * - Horizontal scrollable columns by owner
 * - Card-based display with status/priority indicators
 * - Modal-based add/edit (no side panels)
 * - Inline "+ Add To-Do" per person column
 */

import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CheckSquare, 
  Plus, 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Trash2,
  ListTodo
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserAvatar from '../components/UserAvatar';
import { format, isToday, isTomorrow, isPast, differenceInDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Priority configuration
const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-500', textColor: 'text-red-400' },
  medium: { label: 'Medium', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  low: { label: 'Low', color: 'bg-green-500', textColor: 'text-green-400' },
};

// Format due date for display
const formatDueDate = (dueDate) => {
  if (!dueDate) return null;
  const date = new Date(dueDate);
  
  if (isToday(date)) return { text: 'Today', urgent: true };
  if (isTomorrow(date)) return { text: 'Tomorrow', urgent: false };
  if (isPast(date)) return { text: `${Math.abs(differenceInDays(date, new Date()))}d overdue`, urgent: true };
  
  const days = differenceInDays(date, new Date());
  if (days <= 7) return { text: `${days}d`, urgent: false };
  
  return { text: format(date, 'MMM d'), urgent: false };
};

// To-Do Card Component
const TodoCard = ({ todo, onClick, onComplete }) => {
  const isComplete = todo.status === 'complete';
  const priority = PRIORITY_CONFIG[todo.priority] || PRIORITY_CONFIG.medium;
  const dueInfo = formatDueDate(todo.due_date);
  
  return (
    <Card 
      className={`dark:bg-slate-700/50 border-slate-600 hover:border-blue-400 cursor-pointer transition-all hover:shadow-lg group ${
        isComplete ? 'opacity-60' : ''
      }`}
      onClick={() => onClick(todo)}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onComplete(todo.id);
            }}
            className={`mt-0.5 flex-shrink-0 rounded-full transition-colors ${
              isComplete 
                ? 'text-green-500' 
                : 'text-slate-400 hover:text-green-400'
            }`}
          >
            {isComplete ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <Circle className="h-5 w-5" />
            )}
          </button>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium dark:text-white ${isComplete ? 'line-through text-slate-500' : ''}`}>
              {todo.task}
            </p>
            
            {/* Footer with priority and due date */}
            <div className="flex items-center gap-2 mt-1.5">
              <Badge 
                variant="outline" 
                className={`${priority.color} text-white border-0 text-[10px] px-1.5 py-0`}
              >
                {priority.label}
              </Badge>
              
              {dueInfo && (
                <span className={`text-xs flex items-center gap-1 ${
                  dueInfo.urgent ? 'text-red-400' : 'text-slate-400'
                }`}>
                  <Clock className="h-3 w-3" />
                  {dueInfo.text}
                </span>
              )}
            </div>
            
            {/* Subtasks indicator */}
            {todo.subtasks && todo.subtasks.length > 0 && (
              <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                <ListTodo className="h-3 w-3" />
                <span>
                  {todo.subtasks.filter(s => s.completed).length}/{todo.subtasks.length}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Person Column Component
const PersonColumn = ({ user, todos, onAddTodo, onTodoClick, onCompleteTodo }) => {
  const userTodos = todos.filter(t => t.owner_id === user.id);
  const openTodos = userTodos.filter(t => t.status !== 'complete');
  const completedCount = userTodos.filter(t => t.status === 'complete').length;
  const overdueTodos = openTodos.filter(t => t.due_date && isPast(new Date(t.due_date)));
  
  return (
    <div className="flex-shrink-0 w-72 bg-slate-800/40 rounded-lg border border-slate-700">
      {/* Column Header */}
      <div className="p-3 border-b border-slate-700 sticky top-0 bg-slate-800/90 backdrop-blur-sm rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserAvatar user={user} size="sm" />
            <div>
              <p className="font-medium text-sm dark:text-white">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-400">
                {openTodos.length} open • {completedCount} done
                {overdueTodos.length > 0 && (
                  <span className="text-red-400 ml-1">• {overdueTodos.length} overdue</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* To-Dos List */}
      <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto scrollbar-thin">
        {/* Open To-Dos first */}
        {openTodos.map(todo => (
          <TodoCard 
            key={todo.id} 
            todo={todo} 
            onClick={onTodoClick}
            onComplete={onCompleteTodo}
          />
        ))}
        
        {/* Completed To-Dos */}
        {userTodos.filter(t => t.status === 'complete').slice(0, 3).map(todo => (
          <TodoCard 
            key={todo.id} 
            todo={todo} 
            onClick={onTodoClick}
            onComplete={onCompleteTodo}
          />
        ))}
        
        {userTodos.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-4">No to-dos assigned</p>
        )}
        
        {/* Add To-Do Button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700/50 border border-dashed border-slate-600"
          onClick={() => onAddTodo(user)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add To-Do
        </Button>
      </div>
    </div>
  );
};

const EOSTodos = () => {
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const scrollContainerRef = useRef(null);
  
  // Modal States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [prefilledOwner, setPrefilledOwner] = useState(null);
  
  // Form State
  const [todoForm, setTodoForm] = useState({
    task: '',
    description: '',
    owner_id: '',
    due_date: '',
    priority: 'medium',
    subtasks: []
  });
  
  // Edit Form State
  const [editForm, setEditForm] = useState({});
  const [newSubtask, setNewSubtask] = useState('');

  useEffect(() => {
    fetchTodos();
    fetchUsers();
  }, [filterStatus]);

  const fetchTodos = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      
      const response = await axios.get(`${API}/eos/todos`, { params });
      setTodos(response.data);
    } catch (error) {
      console.error('Error fetching todos:', error);
      toast.error('Failed to load to-dos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Filter to active users who might have to-dos
      const activeUsers = response.data.filter(u => u.is_active !== false);
      setUsers(activeUsers.slice(0, 15)); // Limit for performance
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddTodo = (user) => {
    setPrefilledOwner(user);
    // Set default due date to end of week
    const endOfWeek = new Date();
    endOfWeek.setDate(endOfWeek.getDate() + (5 - endOfWeek.getDay()));
    
    setTodoForm({
      task: '',
      description: '',
      owner_id: user.id,
      due_date: endOfWeek.toISOString().split('T')[0],
      priority: 'medium',
      subtasks: []
    });
    setCreateDialogOpen(true);
  };

  const handleCreateTodo = async () => {
    try {
      if (!todoForm.task || !todoForm.owner_id || !todoForm.due_date) {
        toast.error('Please fill in task, owner, and due date');
        return;
      }

      const payload = {
        ...todoForm,
        due_date: new Date(todoForm.due_date).toISOString()
      };

      await axios.post(`${API}/eos/todos`, payload);
      toast.success('To-Do created');
      setCreateDialogOpen(false);
      resetTodoForm();
      fetchTodos();
    } catch (error) {
      console.error('Error creating todo:', error);
      toast.error(error.response?.data?.detail || 'Failed to create to-do');
    }
  };

  const handleTodoClick = (todo) => {
    setSelectedTodo(todo);
    setEditForm({
      ...todo,
      due_date: todo.due_date ? new Date(todo.due_date).toISOString().split('T')[0] : '',
      subtasks: todo.subtasks || []
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTodo = async () => {
    try {
      const payload = {
        ...editForm,
        due_date: editForm.due_date ? new Date(editForm.due_date).toISOString() : null
      };
      
      await axios.put(`${API}/eos/todos/${selectedTodo.id}`, payload);
      toast.success('To-Do updated');
      setEditDialogOpen(false);
      fetchTodos();
    } catch (error) {
      console.error('Error updating todo:', error);
      toast.error('Failed to update to-do');
    }
  };

  const handleCompleteTodo = async (todoId) => {
    try {
      await axios.patch(`${API}/eos/todos/${todoId}/complete`);
      toast.success('To-Do completed');
      fetchTodos();
    } catch (error) {
      console.error('Error completing todo:', error);
      toast.error('Failed to complete to-do');
    }
  };

  const handleDeleteTodo = async () => {
    try {
      await axios.delete(`${API}/eos/todos/${selectedTodo.id}`);
      toast.success('To-Do deleted');
      setDeleteDialogOpen(false);
      setEditDialogOpen(false);
      setSelectedTodo(null);
      fetchTodos();
    } catch (error) {
      console.error('Error deleting todo:', error);
      toast.error('Failed to delete to-do');
    }
  };

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return;
    setEditForm(prev => ({
      ...prev,
      subtasks: [...(prev.subtasks || []), { title: newSubtask, completed: false }]
    }));
    setNewSubtask('');
  };

  const handleToggleSubtask = (index) => {
    setEditForm(prev => ({
      ...prev,
      subtasks: prev.subtasks.map((s, i) => 
        i === index ? { ...s, completed: !s.completed } : s
      )
    }));
  };

  const handleRemoveSubtask = (index) => {
    setEditForm(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter((_, i) => i !== index)
    }));
  };

  const resetTodoForm = () => {
    setTodoForm({
      task: '',
      description: '',
      owner_id: '',
      due_date: '',
      priority: 'medium',
      subtasks: []
    });
    setPrefilledOwner(null);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Calculate stats
  const stats = {
    total: todos.length,
    open: todos.filter(t => t.status !== 'complete').length,
    completed: todos.filter(t => t.status === 'complete').length,
    overdue: todos.filter(t => t.status !== 'complete' && t.due_date && isPast(new Date(t.due_date))).length,
    dueToday: todos.filter(t => t.status !== 'complete' && t.due_date && isToday(new Date(t.due_date))).length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading To-Dos...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare className="h-8 w-8 text-green-500" />
            <div>
              <h1 className="text-2xl font-bold dark:text-white">Weekly To-Dos</h1>
              <p className="text-sm text-muted-foreground">
                Track weekly commitments by team member
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32 dark:bg-slate-700 dark:border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add To-Do
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex gap-4 p-3 bg-slate-800/40 rounded-lg border border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Open:</span>
            <Badge variant="outline" className="dark:bg-slate-700">{stats.open}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-400 text-sm">Completed:</span>
            <Badge className="bg-green-500">{stats.completed}</Badge>
          </div>
          {stats.dueToday > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-yellow-400 text-sm">Due Today:</span>
              <Badge className="bg-yellow-500">{stats.dueToday}</Badge>
            </div>
          )}
          {stats.overdue > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-red-400 text-sm">Overdue:</span>
              <Badge className="bg-red-500">{stats.overdue}</Badge>
            </div>
          )}
        </div>

        {/* Horizontal Slider Container */}
        <div className="relative">
          {/* Scroll Buttons */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
            onClick={scrollLeft}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
            onClick={scrollRight}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
          
          {/* Scrollable Container */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto px-10 pb-4 scrollbar-thin"
            style={{ scrollbarWidth: 'thin' }}
          >
            {users.map(user => (
              <PersonColumn
                key={user.id}
                user={user}
                todos={todos}
                onAddTodo={handleAddTodo}
                onTodoClick={handleTodoClick}
                onCompleteTodo={handleCompleteTodo}
              />
            ))}
            
            {users.length === 0 && (
              <div className="flex-1 flex items-center justify-center p-8 text-slate-500">
                No team members found.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create To-Do Modal */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-green-500" />
              Create New To-Do
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {prefilledOwner && (
                <span>Adding to-do for {prefilledOwner.first_name} {prefilledOwner.last_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Task *</Label>
              <Input
                value={todoForm.task}
                onChange={(e) => setTodoForm({ ...todoForm, task: e.target.value })}
                placeholder="What needs to be done?"
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Owner *</Label>
              <Select 
                value={todoForm.owner_id} 
                onValueChange={(val) => setTodoForm({ ...todoForm, owner_id: val })}
              >
                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-slate-200">Due Date *</Label>
                <Input
                  type="date"
                  value={todoForm.due_date}
                  onChange={(e) => setTodoForm({ ...todoForm, due_date: e.target.value })}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Priority</Label>
                <Select 
                  value={todoForm.priority} 
                  onValueChange={(val) => setTodoForm({ ...todoForm, priority: val })}
                >
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="high">🔴 High</SelectItem>
                    <SelectItem value="medium">🟡 Medium</SelectItem>
                    <SelectItem value="low">🟢 Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Notes (Optional)</Label>
              <Textarea
                value={todoForm.description}
                onChange={(e) => setTodoForm({ ...todoForm, description: e.target.value })}
                placeholder="Additional context..."
                className="dark:bg-slate-700 dark:border-slate-600"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetTodoForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateTodo}>
              Create To-Do
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit To-Do Modal */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-green-500" />
              Edit To-Do
            </DialogTitle>
          </DialogHeader>
          
          {selectedTodo && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="dark:text-slate-200">Task</Label>
                <Input
                  value={editForm.task || ''}
                  onChange={(e) => setEditForm({ ...editForm, task: e.target.value })}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-slate-200">Status</Label>
                  <Select 
                    value={editForm.status || 'open'} 
                    onValueChange={(val) => setEditForm({ ...editForm, status: val })}
                  >
                    <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                      <SelectItem value="open">⚪ Open</SelectItem>
                      <SelectItem value="complete">✅ Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="dark:text-slate-200">Priority</Label>
                  <Select 
                    value={editForm.priority || 'medium'} 
                    onValueChange={(val) => setEditForm({ ...editForm, priority: val })}
                  >
                    <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                      <SelectItem value="high">🔴 High</SelectItem>
                      <SelectItem value="medium">🟡 Medium</SelectItem>
                      <SelectItem value="low">🟢 Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Due Date</Label>
                <Input
                  type="date"
                  value={editForm.due_date || ''}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              
              {/* Subtasks */}
              <div>
                <Label className="dark:text-slate-200">Subtasks</Label>
                <div className="space-y-2 mt-2">
                  {(editForm.subtasks || []).map((subtask, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-700/50 rounded">
                      <input
                        type="checkbox"
                        checked={subtask.completed}
                        onChange={() => handleToggleSubtask(idx)}
                        className="rounded"
                      />
                      <span className={`flex-1 text-sm ${subtask.completed ? 'line-through text-slate-500' : 'dark:text-white'}`}>
                        {subtask.title}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSubtask(idx)}
                        className="h-6 w-6 p-0 text-slate-400 hover:text-red-400"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="flex gap-2">
                    <Input
                      value={newSubtask}
                      onChange={(e) => setNewSubtask(e.target.value)}
                      placeholder="Add subtask..."
                      className="dark:bg-slate-700 dark:border-slate-600"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                    />
                    <Button size="sm" onClick={handleAddSubtask}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Notes</Label>
                <Textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Additional notes..."
                  className="dark:bg-slate-700 dark:border-slate-600"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateTodo}>
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">Delete To-Do?</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-slate-400">
              This action cannot be undone. This will permanently delete this to-do.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-slate-700 dark:border-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteTodo}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default EOSTodos;
